import { Button, type ButtonProps } from "@/components/ui/button";
import useTracker from "@/tracking/useTracker";
import { cn } from "@/utils/cn";
import React from "react";

export const CartUITitle = ({ children }: { children: React.ReactNode }) => (
  <h2
    data-component="CheckoutCheckoutCartUiTitle"
    className="items-center font-archivo text-base leading-[108%] font-semibold"
  >
    {children}
  </h2>
);

// -----------------------------

export const CartUISection = ({ children }: { children: React.ReactNode }) => (
  <>{children}</>
);

// -----------------------------

export const CartUIHeader = ({
  title,
  note,
  children,
}: {
  title?: React.ReactNode;
  note?: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <div
    data-component="CheckoutCheckoutCartUiHeader"
    className={cn("flex flex-wrap items-center justify-between gap-4")}
  >
    {title && (
      <div className="flex items-center gap-2">
        <CartUITitle>{title}</CartUITitle>
        {note && (
          <span className="font-archivo text-xs leading-[120%] font-light tracking-[0.24px] text-type-2 italic">
            • {note}
          </span>
        )}
      </div>
    )}
    {children}
  </div>
);

// -----------------------------

export const CartUIItems = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      data-component="CheckoutCartUIItems"
      className="space-y-4 md:space-y-4"
    >
      {children}
    </div>
  );
};

// -----------------------------

export const CartUIDivider = ({ className }: { className?: string }) => (
  <div data-component="CartUIUIDivider" className={cn(className)}>
    <div className="h-px w-full bg-[#EAEAEA]" />
  </div>
);

// -----------------------------

const variantMap = {
  danger: "inline-danger",
  accent: "inline-accent",
  remove: "inline-base-underline",
  muted: "inline-muted",
} as const;

type CartUICTAProps = Omit<ButtonProps, "variant"> & {
  variant?: keyof typeof variantMap;
  event?: string;
};

export const CartUICTA = ({
  variant = "accent",
  event,
  onClick,
  ...props
}: CartUICTAProps) => {
  const { track } = useTracker();

  return (
    <Button
      variant={variantMap[variant]}
      onClick={(e) => {
        if (event) track(event);
        onClick?.(e);
      }}
      {...props}
    />
  );
};
